import * as THREE from 'three';
import type { SketchFace, SketchSolid, SketchVec3 } from './types';
import { solidToGeometry, toolEpsilon } from './primitives';
import { csgUnion } from './csgUtil';
import { meshToFaces } from './meshToSolid';
/**
 * Follow Me (sweep) engine.
 *
 * A profile face rides a polyline path with PARALLEL TRANSPORT (the profile
 * frame rotates minimally from segment to segment, avoiding Frenet flips).
 * Each path step materializes a rotated copy of the profile loops; adjacent
 * copies are connected with side quads; BOTH ends get caps — the root is
 * sunk one toolEpsilon BEHIND the profile plane first, so the sweep body is
 * a closed tool overlapping its union target (tangent/open-ended tools are
 * three-bvh-csg's failure mode; same padded-tool contract as Push/Pull).
 * The union is rebuilt into analytic faces through meshToSolid's repair
 * pipeline.
 */

const asTuple = (p: THREE.Vector3): SketchVec3 => [p.x, p.y, p.z];

/** Rotation that carries direction a onto direction b (unit inputs). */
const quatBetween = (a: THREE.Vector3, b: THREE.Vector3): THREE.Quaternion =>
    new THREE.Quaternion().setFromUnitVectors(a.clone().normalize(), b.clone().normalize());

export interface SweepResult {
    ok: boolean;
    solid?: SketchSolid;
    reason?: string;
}

/**
 * Sweep the face `faceId` of `solid` along `path` (world-space polyline).
 * The profile plane must be near-perpendicular to the path's first segment
 * (the Follow Me contract); the caller enforces/steers that interactively.
 */
export function sweepFace(
    solid: SketchSolid,
    faceId: string,
    path: THREE.Vector3[]
): SweepResult {
    const face = solid.faces.find((f) => f.id === faceId);
    if (!face) return { ok: false, reason: 'Profile face not found.' };
    if (path.length < 2) return { ok: false, reason: 'Path needs at least two points.' };

    const profileNormal = new THREE.Vector3(...face.normal).normalize();
    const profileOrigin = new THREE.Vector3(...face.origin);

    // Parallel-transported frame along the path: each station's orientation
    // is the previous one rotated minimally onto the next segment direction
    // (world-axis composition, applied to the profile's local coordinates).
    const orientations: THREE.Quaternion[] = [];
    const positions: THREE.Vector3[] = path.map((p) => p.clone());
    orientations.push(quatBetween(profileNormal, path[1].clone().sub(path[0])));
    for (let i = 1; i < positions.length; i++) {
        const prevDir = positions[i].clone().sub(positions[i - 1]);
        const nextDir = positions[i + 1]
            ? positions[i + 1].clone().sub(positions[i])
            : prevDir;
        const step = prevDir.lengthSq() > 1e-12 && nextDir.lengthSq() > 1e-12
            ? quatBetween(prevDir, nextDir)
            : new THREE.Quaternion();
        orientations.push(step.multiply(orientations[i - 1].clone()));
    }

    // Closed tool: root station sunk one epsilon BEHIND the profile plane
    // (same frame as station 0) so the body overlaps the solid instead of
    // touching it tangentially, and the open start end gets a real cap.
    const eps = toolEpsilon(solid);
    const rootDir = path[1].clone().sub(path[0]).normalize();
    positions.unshift(positions[0].clone().addScaledVector(rootDir, -eps));
    orientations.unshift(orientations[0].clone());

    // Materialize rotated profile copies at every path station.
    const copies: SketchVec3[][] = [];
    for (let station = 0; station < positions.length; station++) {
        const rotated: SketchVec3[] = face.outer.map((p) => {
            const local = new THREE.Vector3(...p).sub(profileOrigin);
            return asTuple(positions[station].clone().add(local.applyQuaternion(orientations[station])));
        });
        copies.push(rotated);
    }

    // Side walls between consecutive copies (one quad per contour edge).
    const sideFaces: SketchFace[] = [];
    for (let station = 0; station < copies.length - 1; station++) {
        const A = copies[station];
        const B = copies[station + 1];
        for (let i = 0; i < A.length; i++) {
            const j = (i + 1) % A.length;
            const a = new THREE.Vector3(...A[i]);
            const b = new THREE.Vector3(...A[j]);
            const c = new THREE.Vector3(...B[j]);
            const d = new THREE.Vector3(...B[i]);
            const n = new THREE.Vector3().subVectors(b, a).cross(new THREE.Vector3().subVectors(d, a)).normalize();
            sideFaces.push({
                id: `sweep-side-${station}-${i}`,
                origin: asTuple(a),
                normal: asTuple(n),
                outer: [asTuple(a), asTuple(b), asTuple(c), asTuple(d)],
                holes: [],
            });
        }
    }

    // End cap at the last station (normal along the final path direction).
    const lastDir = path[path.length - 1].clone().sub(path[path.length - 2]).normalize();
    const capFace: SketchFace = {
        id: 'sweep-cap',
        origin: asTuple(positions[positions.length - 1]),
        normal: asTuple(lastDir),
        outer: copies[copies.length - 1],
        holes: [],
    };

    // Root cap at the sunk start station (closes the tool's near end).
    const rootFace: SketchFace = {
        id: 'sweep-root',
        origin: asTuple(positions[0]),
        normal: asTuple(rootDir.clone().negate()),
        outer: copies[0],
        holes: [],
    };

    // Sweep body as a standalone CLOSED shell, unioned onto the solid.
    const bodyFaces: SketchFace[] = [rootFace, ...sideFaces, capFace];
    const body = solidToGeometry({ ...solid, faces: bodyFaces });
    const solidGeo = solidToGeometry(solid);
    const merged = csgUnion(solidGeo, body);

    const rebuilt = meshToFaces(merged);
    if (!rebuilt.ok) return { ok: false, reason: rebuilt.reason };

    return {
        ok: true,
        solid: {
            ...solid,
            faces: rebuilt.faces.map((f) => ({ ...f, id: `${face.id}-sweep-${f.id}` })),
        },
    };
}
